import type { RefObject } from 'react';
import { useCallback, useMemo } from 'react';

import { convertSelectionToLegacyFormat } from '@confluence/highlight-actions/entry-points/convertSelectionToLegacyFormat.ts';
import {
	useGetContentLastModifiedTimestamp,
	useSetInsertStorageFragmentRequestPayload,
	useSetIsCreatedFromTable,
	useSetIsCreatedWithAi,
	useSetIsRovoPanelWidth,
} from '@confluence/issue-create-side-panel/entry-points/BulkCreateContextProvider.ts';
import { useIssueCreateSidePanelContext } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import {
	constructInsertStorageFragmentRequestPayload,
	getPageContentRange,
	getValidRange,
} from '@confluence/issue-create-side-panel/entry-points/issueInsertionUtils.ts';
import {
	BULK_ISSUE_CREATE_SIDE_PANEL_ID,
	BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH,
	ROVO_BULK_CREATE_SIDE_PANEL_WIDTH,
} from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanelConstants.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';

type UseBulkIssueCreateProps = {
	contentId: string;
	content: RefObject<any>;
	useRovoWidth?: boolean;
};

export const useBulkIssueCreate = ({
	contentId,
	content,
	useRovoWidth = false,
}: UseBulkIssueCreateProps): (() => void) => {
	const rightSidebarContext = useRightSidebarContext();
	const setIsCreatedWithAi = useSetIsCreatedWithAi();
	const setIsCreatedFromTable = useSetIsCreatedFromTable();
	const setIsRovoPanelWidth = useSetIsRovoPanelWidth();
	const setInsertStorageFragmentRequestPayload = useSetInsertStorageFragmentRequestPayload();
	const contentLastModifiedTimestamp = useGetContentLastModifiedTimestamp();
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, issueCreateSidePanelActions] = useIssueCreateSidePanelContext();
	const { pageInfo } = usePageInfo({ fetchPolicy: 'cache-first' });

	const hasEditPermission = useMemo(
		() =>
			pageInfo?.operations?.some(
				(op) => op?.operation === 'update' && op?.targetType === pageInfo.type,
			) || false,
		[pageInfo],
	);

	return useCallback(() => {
		void setIsCreatedWithAi(true);
		void setIsCreatedFromTable(false);
		void setIsRovoPanelWidth(useRovoWidth);
		if (content.current) {
			const range = getValidRange(getPageContentRange());
			const payload = constructInsertStorageFragmentRequestPayload(
				contentId,
				convertSelectionToLegacyFormat,
				range,
				content.current,
				contentLastModifiedTimestamp,
			);
			void setInsertStorageFragmentRequestPayload(payload);
		}
		void issueCreateSidePanelActions.setHasEditPermission(hasEditPermission);
		rightSidebarContext.open(
			BULK_ISSUE_CREATE_SIDE_PANEL_ID,
			useRovoWidth ? ROVO_BULK_CREATE_SIDE_PANEL_WIDTH : BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH,
		);
	}, [
		setIsCreatedWithAi,
		setIsCreatedFromTable,
		setIsRovoPanelWidth,
		useRovoWidth,
		contentId,
		content,
		contentLastModifiedTimestamp,
		setInsertStorageFragmentRequestPayload,
		issueCreateSidePanelActions,
		hasEditPermission,
		rightSidebarContext,
	]);
};
